// The weekly hat, from the screens (PLAN-2026-10-30-the-weekly-hat): the
// league on screen's hat, from the hub card a real league's every view
// carries (`LeagueRef.hat`) or the simulator's for the practice league, and a
// pick by the week's high scorer.

import { useMemo } from 'react';
import { LeagueHat as LeagueHatSchema, type LeagueHat, type WeeklyHat } from '@fsp/domain';
import { messageOf, send } from './api';
import { useRouteLeagueId } from './hooks';
import { useActiveLeague, useLeagues } from './LeaguesProvider';
import { useLeagueSource } from './leagueSource';
import { movesChanged } from './moves';
import { useSim } from './SimProvider';

/** The hat in the league on screen, or null for none (or the switch off). */
export function useLeagueHat(): LeagueHat | null {
  const source = useLeagueSource();
  const card = useActiveLeague();
  const leagueId = useRouteLeagueId();
  const { state, version } = useSim();
  return useMemo(
    () =>
      source === 'api'
        ? (card?.league.hat ?? null)
        : // ONLY THE PRACTICE LEAGUE ON SCREEN: off a league's address the
          // simulator answers with its first league, and its team ids are
          // every practice league's (`t1` is mine in each), so its hat would
          // land on teams in the others (the review of the hat).
          source === 'sim' &&
            leagueId !== null &&
            state.leagueId === leagueId &&
            state.settings.fun.hat.enabled
          ? state.hat
          : null,
    // `version` is the simulator's change counter: its state is changed in place.
    [source, card, leagueId, state, version],
  );
}

/**
 * Put a hat on the week's low scorer, as its high scorer. Resolves to null
 * when it is on, or to the sentence to show.
 */
export function usePickHat(): (hat: WeeklyHat) => Promise<string | null> {
  const source = useLeagueSource();
  const card = useActiveLeague();
  const leagues = useLeagues();
  const { pickHat } = useSim();
  return async (hat) => {
    if (source === 'sim') return pickHat(hat) ? null : 'Only the week’s high scorer picks the hat.';
    if (card === null) return 'There is no league here.';
    const leagueId = card.league.id;
    const reply = await send(
      'PUT',
      `/leagues/${encodeURIComponent(leagueId)}/hat`,
      { hat },
      LeagueHatSchema,
    );
    // ASKED AGAIN WHATEVER CAME BACK: every view carries the hat, the hub's
    // card first, and the chat has a new line.
    leagues.reload();
    setTimeout(() => movesChanged(leagueId), 0);
    if (reply.status === 'ok') return null;
    return messageOf(reply, 'That hat did not go on.', 'Could not reach the site. No hat changed.');
  };
}
